import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

import type { FocusTarget } from "./use-meal-form";

type UseMealFocusOptions = {
  isBlocked: boolean;
};

export type UseMealFocusResult = {
  foodInputRef: RefObject<HTMLInputElement | null>;
  quantityInputRef: RefObject<HTMLInputElement | null>;
  focusNow: (target: FocusTarget) => void;
  queueFocus: (target: FocusTarget | null) => void;
};

// Centralizes focus management for the meal form so caller code can signal
// which input should receive focus even if the DOM is not ready yet (e.g.
// right after state updates or before a modal unblocks interaction).
export const useMealFocus = ({ isBlocked }: UseMealFocusOptions): UseMealFocusResult => {
  const foodInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const [pendingFocus, setPendingFocus] = useState<FocusTarget | null>(null);

  const schedule = useCallback((callback: () => void) => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => callback());
      return;
    }

    setTimeout(callback, 0);
  }, []);

  const focusNow = useCallback(
    (target: FocusTarget) => {
      const resolveInput = () => (target === "food" ? foodInputRef.current : quantityInputRef.current);

      schedule(() => {
        const input = resolveInput();
        if (!input) {
          return;
        }

        input.focus();
        if (typeof input.select === "function") {
          input.select();
        }
      });
    },
    [schedule],
  );

  useEffect(() => {
    if (!pendingFocus || isBlocked) {
      return;
    }

    // Once the form is interactive again, flush the queued focus request.
    focusNow(pendingFocus);
    setPendingFocus(null);
  }, [focusNow, isBlocked, pendingFocus]);

  // Allows callers to enqueue a focus target that will run after any blockers
  // (e.g. popovers or async transitions) clear.
  const queueFocus = useCallback((target: FocusTarget | null) => {
    setPendingFocus(target);
  }, []);

  return {
    foodInputRef,
    quantityInputRef,
    focusNow,
    queueFocus,
  };
};
